// plan-05 S03: SVG render — base hexes + maturity coloring, re-skinned in CRT palette

import type { HexGrid } from './geometry'
import { hexPoints } from './geometry'
import type { MaturityResult } from './maturity'

export const SVG_NS = 'http://www.w3.org/2000/svg'

// palette re-skin from v1: stepped green-amber-golden ramp
const MATURITY_COLORS: Array<{ fill: string; stroke: string }> = [
  // 0: base
  { fill: 'rgba(0, 90, 0, 0.10)', stroke: 'rgba(0, 90, 0, 0.28)' },
  // 1: king_green
  { fill: 'rgba(0, 180, 0, 0.50)', stroke: 'rgba(0, 180, 0, 0.85)' },
  // 2: king_green brighter
  { fill: 'rgba(0, 210, 0, 0.65)', stroke: 'rgba(0, 210, 0, 0.95)' },
  // 3: term_green
  { fill: 'rgba(0, 210, 0, 0.85)', stroke: 'rgba(0, 210, 0, 0.95)' },
  // 4: amber
  { fill: 'rgba(0, 176, 255, 0.72)', stroke: 'rgba(0, 176, 255, 0.95)' },
  // 5: amber saturated
  { fill: 'rgba(0, 176, 255, 0.92)', stroke: 'rgba(0, 176, 255, 0.95)' },
  // 6+: golden hot zone
  { fill: 'rgba(40, 180, 220, 0.95)', stroke: 'rgba(40, 180, 220, 0.95)' },
]

function maturityColor(hits: number): { fill: string; stroke: string } {
  if (hits === 0) return MATURITY_COLORS[0]
  if (hits >= 6) return MATURITY_COLORS[6]
  return MATURITY_COLORS[hits]
}

// flash color for animation — one step brighter
function flashColor(hits: number): { fill: string; stroke: string } {
  if (hits === 0) return { fill: 'rgba(0, 210, 0, 0.80)', stroke: 'rgba(0, 210, 0, 1.00)' }
  if (hits >= 5) return { fill: 'rgba(220, 180, 40, 0.95)', stroke: 'rgba(220, 180, 40, 1.00)' }
  const stepped = { fill: 'rgba(0, 210, 0, 0.90)', stroke: 'rgba(0, 210, 0, 1.00)' }
  if (hits >= 3) return { fill: 'rgba(0, 176, 255, 0.90)', stroke: 'rgba(0, 176, 255, 1.00)' }
  return stepped
}

export function createSVGDefs(): SVGDefsElement {
  const defs = document.createElementNS(SVG_NS, 'defs') as SVGDefsElement
  defs.innerHTML = `
    <filter id="vt-golden-glow" x="-60%" y="-60%" width="220%" height="220%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur"/>
      <feColorMatrix in="blur" type="matrix"
        values="1.5 0.8 0   0 0
                0.8 0.6 0   0 0
                0   0   0   0 0
                0   0   0  18 -7" result="glow"/>
      <feMerge><feMergeNode in="glow"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
    <filter id="vt-ring-glow" x="-80%" y="-80%" width="260%" height="260%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur"/>
      <feColorMatrix in="blur" type="matrix"
        values="2   1   0   0 0
                1   0.7 0   0 0
                0   0   0   0 0
                0   0   0  12 -4" result="glow"/>
      <feMerge><feMergeNode in="glow"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>`
  return defs
}

export interface HeatmapSVG {
  svg: SVGSVGElement
  cellsGroup: SVGGElement
  linesGroup: SVGGElement
  chainGroup: SVGGElement
  primeLinesGroup: SVGGElement
  ringGroup: SVGGElement
}

export function createHeatmapSVG(grid: HexGrid): HeatmapSVG {
  const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement
  // plan-14 S04: scale via viewBox + 100% width; no fixed width/height attributes
  svg.setAttribute('viewBox', `0 0 ${grid.width} ${grid.height}`)
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
  svg.style.display = 'block'
  svg.style.width = '100%'
  svg.style.height = 'auto'
  svg.style.maxWidth = '100%'

  // defs
  svg.appendChild(createSVGDefs())

  // cells layer
  const cellsGroup = document.createElementNS(SVG_NS, 'g') as SVGGElement
  cellsGroup.setAttribute('id', 'vt-hex-cells')
  svg.appendChild(cellsGroup)

  // golden ring layer — above cells, below lines
  const ringGroup = document.createElementNS(SVG_NS, 'g') as SVGGElement
  ringGroup.setAttribute('id', 'vt-golden-rings')
  svg.appendChild(ringGroup)

  // lines layer
  const linesGroup = document.createElementNS(SVG_NS, 'g') as SVGGElement
  linesGroup.setAttribute('id', 'vt-hex-connection-lines')
  svg.appendChild(linesGroup)

  // maturity chain
  const chainGroup = document.createElementNS(SVG_NS, 'g') as SVGGElement
  chainGroup.setAttribute('id', 'vt-maturity-chain')
  svg.appendChild(chainGroup)

  // prime-position lines
  const primeLinesGroup = document.createElementNS(SVG_NS, 'g') as SVGGElement
  primeLinesGroup.setAttribute('id', 'vt-prime-position-lines')
  svg.appendChild(primeLinesGroup)

  // build hex polygons
  const { fill: baseFill, stroke: baseStroke } = MATURITY_COLORS[0]
  for (const h of grid.ordered) {
    const poly = document.createElementNS(SVG_NS, 'polygon') as SVGPolygonElement
    poly.setAttribute('points', hexPoints(h.cx, h.cy))
    poly.setAttribute('fill', baseFill)
    poly.setAttribute('stroke', baseStroke)
    poly.setAttribute('stroke-width', '0.8')
    poly.dataset.id = String(h.seqNum)
    poly.dataset.col = String(h.col)
    poly.dataset.row = String(h.row)
    h.poly = poly
    cellsGroup.appendChild(poly)
  }

  return { svg, cellsGroup, linesGroup, chainGroup, primeLinesGroup, ringGroup }
}

export function resetColors(grid: HexGrid, groups: HeatmapSVG): void {
  const { fill, stroke } = MATURITY_COLORS[0]
  for (const h of grid.ordered) {
    h.poly!.setAttribute('fill', fill)
    h.poly!.setAttribute('stroke', stroke)
    h.poly!.removeAttribute('filter')
  }
  clearGroup(groups.linesGroup)
  clearGroup(groups.primeLinesGroup)
  clearGroup(groups.chainGroup)
  clearGroup(groups.ringGroup)
}

export function applyMaturityColors(
  grid: HexGrid,
  result: MaturityResult,
  _groups: HeatmapSVG,
): void {
  for (const h of grid.ordered) {
    const { fill, stroke } = maturityColor(result.hits[h.seqNum])
    h.poly!.setAttribute('fill', fill)
    h.poly!.setAttribute('stroke', stroke)
    if (result.goldenGlow.has(h.seqNum)) {
      h.poly!.setAttribute('filter', 'url(#vt-golden-glow)')
    } else {
      h.poly!.removeAttribute('filter')
    }
  }
}

export { maturityColor, flashColor, MATURITY_COLORS }

function clearGroup(g: SVGGElement): void {
  while (g.firstChild) g.removeChild(g.firstChild)
}

export { clearGroup }
